import '/css/style.css';
import '/css/navigation.css';
import '/css/vimeo.css';
import { setupNavigation, cleanupNavigation } from '../UI/navigation.js';

const FEED_ENDPOINT = '/server/vimeo-feed.php';
const DEFAULT_USER = 'user127893038';
const DEFAULT_LIMIT = 12;
const FEED_TIMEOUT_MS = 9000;
const pageRouteMap = {
  home: '/',
  market: '/market',
  catering: '/catering',
  boutique: '/boutique',
  about: '/about',
  games: '/games'
};

const shell = document.getElementById('vimeo-feed-shell');
const statusElement = document.getElementById('vimeo-feed-status');
const gridElement = document.getElementById('vimeo-feed-grid');
let vimeoApiLoadPromise = null;

const accountHandle = (shell?.dataset.vimeoUser || DEFAULT_USER).trim() || DEFAULT_USER;
const onNavigateHome = () => {
  window.location.href = pageRouteMap.home;
};
const onNavigateSection = e => {
  const target = pageRouteMap[e.detail?.section];
  if (target) window.location.href = target;
};

window.addEventListener('navigate-home', onNavigateHome);
window.addEventListener('navigate-section', onNavigateSection);

window.addEventListener(
  'pagehide',
  () => {
    stopAllVideoPlayback().catch(() => {});
    window.removeEventListener('navigate-home', onNavigateHome);
    window.removeEventListener('navigate-section', onNavigateSection);
    cleanupNavigation();
  },
  { once: true }
);

setupNavigation();
loadVimeoPlayerApi().catch(() => {});
setupVideoCardInteractions();
loadFeed(accountHandle);

async function loadFeed(user) {
  if (!gridElement) return;

  setStatus('Loading Vimeo feed...');
  gridElement.innerHTML = '';
  const loaders = [() => loadFeedFromServer(user), () => loadFeedFromVimeoLegacyApi(user)];
  let videos = [];
  let lastError = null;

  for (const load of loaders) {
    try {
      videos = await load();
      if (Array.isArray(videos) && videos.length > 0) {
        break;
      }
    } catch (error) {
      lastError = error;
    }
  }

  if (Array.isArray(videos) && videos.length > 0) {
    renderFeed(videos);
    setStatus('');
    return;
  }

  console.error('[Vimeo Feed] Could not load feed', lastError);
  setStatus('Could not load videos right now.');
  renderFallbackLink(user);
}

async function loadFeedFromServer(user) {
  const query = new URLSearchParams({ user, limit: String(DEFAULT_LIMIT) });
  const response = await fetchWithTimeout(`${FEED_ENDPOINT}?${query.toString()}`, {
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`Server feed request failed (${response.status})`);
  }

  const payload = await response.json();
  const videos = Array.isArray(payload?.videos) ? payload.videos : [];
  if (!payload?.ok || videos.length === 0) {
    throw new Error(payload?.error || 'No videos from server feed');
  }

  return videos;
}

async function loadFeedFromVimeoLegacyApi(user) {
  const response = await fetchWithTimeout(
    `https://vimeo.com/api/v2/${encodeURIComponent(user)}/videos.json`,
    {
      headers: { Accept: 'application/json' }
    }
  );

  if (!response.ok) {
    throw new Error(`Legacy Vimeo API failed (${response.status})`);
  }

  const data = await response.json();
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('Legacy Vimeo API returned no videos');
  }

  return data.slice(0, DEFAULT_LIMIT).map(item => ({
    id: item.id,
    title: item.title,
    description: item.description,
    url: item.url,
    thumbnail: item.thumbnail_large || item.thumbnail_medium || item.thumbnail_small || '',
    duration: item.duration,
    uploadDate: item.upload_date
  }));
}

function renderFeed(videos) {
  if (!gridElement) return;

  const markup = videos
    .map(video => {
      const videoId = extractVideoId(video);
      if (!videoId) return '';

      const cleanTitle = cleanVideoTitle(video.title || 'Untitled');
      const safeTitle = escapeHtml(cleanTitle);
      const rawDescription = truncate((video.description || '').trim(), 180);
      const safeDescription = escapeHtml(rawDescription);
      const thumbnailUrl = sanitizeUrl(video.thumbnail);
      const thumbMarkup = thumbnailUrl
        ? `<img class="vimeo-thumb" src="${thumbnailUrl}" alt="${safeTitle}" loading="lazy" />`
        : `<div class="vimeo-thumb vimeo-thumb-placeholder">${safeTitle}</div>`;

      return `
        <article class="vimeo-card">
          <div class="vimeo-player-wrap" data-video-id="${videoId}" data-video-title="${safeTitle}">
            <button type="button" class="vimeo-play-btn" aria-label="Play ${safeTitle}">
              ${thumbMarkup}
              <span class="vimeo-play-icon" aria-hidden="true">▶</span>
            </button>
          </div>
          <h2 class="vimeo-card-title">${safeTitle}</h2>
          ${safeDescription ? `<p class="vimeo-card-description">${safeDescription}</p>` : ''}
        </article>
      `;
    })
    .join('');

  gridElement.innerHTML = markup || '<p class="vimeo-feed-status">No videos found.</p>';
  cacheVideoPlaceholders();
}

function extractVideoId(video) {
  const direct = String(video?.id || '').match(/\d+/);
  if (direct) return direct[0];

  const fromUrl = String(video?.url || '').match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (fromUrl) return fromUrl[1];

  return '';
}

function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

function setStatus(text) {
  if (!statusElement) return;
  statusElement.textContent = text;
}

function setupVideoCardInteractions() {
  if (!gridElement) return;

  gridElement.addEventListener('click', async event => {
    const playButton = event.target.closest('.vimeo-play-btn');
    if (playButton) {
      const wrapper = playButton.closest('.vimeo-player-wrap');
      if (!wrapper) return;

      const videoId = wrapper.dataset.videoId || '';
      const safeTitle = wrapper.dataset.videoTitle || 'Vimeo video';
      if (!videoId) return;

      await stopOtherVideoPlayback(wrapper);
      const { iframe, player } = await startPlayback(wrapper, videoId, safeTitle);
      try {
        if (player?.requestFullscreen) {
          await player.play().catch(() => {});
          await player.requestFullscreen();
        } else {
          await requestElementFullscreen(iframe);
        }
      } catch (error) {
        console.warn('[Vimeo Feed] Fullscreen failed', error);
      }
    }
  });
}

function cacheVideoPlaceholders() {
  if (!gridElement) return;
  const wrappers = gridElement.querySelectorAll('.vimeo-player-wrap');
  wrappers.forEach(wrapper => {
    wrapper.__placeholderHtml = wrapper.innerHTML;
  });
}

async function stopOtherVideoPlayback(activeWrapper) {
  if (!gridElement) return;
  const wrappers = Array.from(gridElement.querySelectorAll('.vimeo-player-wrap'));
  await Promise.allSettled(
    wrappers
      .filter(wrapper => wrapper !== activeWrapper)
      .map(wrapper => stopWrapperPlayback(wrapper))
  );
}

async function stopAllVideoPlayback() {
  if (!gridElement) return;
  const wrappers = Array.from(gridElement.querySelectorAll('.vimeo-player-wrap'));
  await Promise.allSettled(wrappers.map(wrapper => stopWrapperPlayback(wrapper)));
}

async function stopWrapperPlayback(wrapper) {
  if (!wrapper) return;
  const player = wrapper.__vimeoPlayer || null;
  if (player) {
    if (typeof player.pause === 'function') {
      await player.pause().catch(() => {});
    }
    if (typeof player.unload === 'function') {
      await player.unload().catch(() => {});
    }
    wrapper.__vimeoPlayer = null;
  }

  if (wrapper.querySelector('iframe')) {
    wrapper.innerHTML = wrapper.__placeholderHtml || '';
  }
}

async function startPlayback(wrapper, videoId, safeTitle) {
  const existingIframe = wrapper.querySelector('iframe');
  if (existingIframe) {
    const existingPlayer = wrapper.__vimeoPlayer || null;
    return { iframe: existingIframe, player: existingPlayer };
  }

  wrapper.innerHTML = `
    <iframe
      src="https://player.vimeo.com/video/${videoId}?autoplay=1&dnt=1"
      title="${safeTitle}"
      loading="lazy"
      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
      referrerpolicy="strict-origin-when-cross-origin"
      allowfullscreen
    ></iframe>
  `;

  const iframe = wrapper.querySelector('iframe');
  const player = await createVimeoPlayer(iframe);
  wrapper.__vimeoPlayer = player;
  return { iframe, player };
}

async function requestElementFullscreen(element) {
  if (!element) return;
  if (element.requestFullscreen) {
    await element.requestFullscreen();
    return;
  }
  if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen();
  }
}

async function createVimeoPlayer(iframe) {
  if (!iframe) return null;
  try {
    const VimeoNs = await loadVimeoPlayerApi();
    if (!VimeoNs?.Player) return null;
    return new VimeoNs.Player(iframe);
  } catch {
    return null;
  }
}

async function loadVimeoPlayerApi() {
  if (window.Vimeo?.Player) {
    return window.Vimeo;
  }
  if (vimeoApiLoadPromise) {
    return vimeoApiLoadPromise;
  }

  vimeoApiLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-vimeo-player-api="1"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.Vimeo), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Vimeo Player API')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    script.defer = true;
    script.dataset.vimeoPlayerApi = '1';
    script.onload = () => resolve(window.Vimeo);
    script.onerror = () => reject(new Error('Failed to load Vimeo Player API'));
    document.head.appendChild(script);
  });

  return vimeoApiLoadPromise;
}

function renderFallbackLink(user) {
  if (!gridElement) return;
  const profile = `https://vimeo.com/${encodeURIComponent(user)}`;
  gridElement.innerHTML = `
    <article class="vimeo-card">
      <h2 class="vimeo-card-title">Open Vimeo Profile</h2>
      <p class="vimeo-card-description">The feed could not be loaded automatically.</p>
      <a class="vimeo-card-link" href="${profile}" target="_blank" rel="noreferrer">
        Go to ${escapeHtml(user)} on Vimeo
      </a>
    </article>
  `;
}

async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FEED_TIMEOUT_MS);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

function sanitizeUrl(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
    return parsed.href;
  } catch {
    return '';
  }
}

function cleanVideoTitle(title) {
  return String(title)
    .replace(/\s*\.(mp4|mov|m4v|webm|avi|mkv|wmv|mpeg|mpg|ogg)\s*$/i, '')
    .trim();
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
